import React, { useEffect, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { Bar, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
);

//const IP = "http://localhost:5020";
const IP = "https://reportes.aux-rolplay.com";

const Histograma = ({ isOpen, onClose }) => {
  const [histData, setHistData] = useState([]);
  const [viewMode, setViewMode] = useState("daily");
  const [loginData, setLoginData] = useState([]); // Nuevo estado para logins

  useEffect(() => {
    if (viewMode === "logins") {
      fetch(`${IP}/somos/historicoslogin`)
        .then((res) => res.json())
        .then((data) => {
          // data es un objeto con claves 0-23
          const processed = processLoginData(data);
          setLoginData(processed);
        })
        .catch((err) => console.error("Error fetching login data:", err));
    } else {
      fetch(`${IP}/somos/histograma`)
        .then((res) => res.json())
        .then((data) => {
          const processedData =
            viewMode === "daily"
              ? processHistogramData(data.dataSimv2)
              : viewMode === "hourly"
                ? processHourlyHistogramData(data.dataSimv2)
                : processHistoricData(data.dataSimv2);
          setHistData(processedData);
        })
        .catch((err) => console.error("Error fetching histogram data:", err));
    }
  }, [viewMode]);

  const processHistogramData = (data) => {
    // Asegura que siempre existan las 4 claves correctas, incluyendo Protagonista
    const grouped = data.reduce((acc, item) => {
      const date = item.sim_datetime.split(" ")[0];
      if (!acc[date]) {
        acc[date] = {
          Protagonista: 0,
          Espectador: 0,
          Conocedor: 0,
          Explorador: 0,
        };
      }
      // Normaliza Promotor y Protagonista a Protagonista
      let cat = item.sim_category;
      if (cat === "Promotor") cat = "Protagonista";
      if (
        cat === "Protagonista" ||
        cat === "Espectador" ||
        cat === "Conocedor" ||
        cat === "Explorador"
      ) {
        acc[date][cat]++;
      }
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
      .slice(0, 10);
  };

  const processHourlyHistogramData = (data) => {
    // Mapeo consistente de categorías, incluyendo Protagonista
    const categoryMap = {
      Promotor: "Protagonista",
      Protagonista: "Protagonista",
      Conocedor: "Conocedor",
      Explorador: "Explorador",
      Espectador: "Espectador",
    };

    const grouped = data.reduce((acc, item) => {
      const hour = new Date(item.sim_datetime).getHours();
      const hourString = `${hour.toString().padStart(2, "0")}:00`;

      if (!acc[hourString]) {
        acc[hourString] = {
          Protagonista: 0,
          Conocedor: 0,
          Explorador: 0,
          Espectador: 0,
        };
      }
      // Solo sumar si la categoría existe en el mapeo
      const mappedCategory = categoryMap[item.sim_category];
      if (mappedCategory) {
        acc[hourString][mappedCategory]++;
      }
      return acc;
    }, {});

    // Asegurar que tenemos todas las horas del día (0-23)
    for (let i = 0; i < 24; i++) {
      const hourString = `${i.toString().padStart(2, "0")}:00`;
      if (!grouped[hourString]) {
        grouped[hourString] = {
          Protagonista: 0,
          Conocedor: 0,
          Explorador: 0,
          Espectador: 0,
        };
      }
    }

    return Object.entries(grouped).sort(([hourA], [hourB]) =>
      hourA.localeCompare(hourB),
    );
  };

  // NUEVA función para histórico: total por hora
  const processHistoricData = (data) => {
    // Agrupa por hora (00:00, 01:00, ..., 23:00)
    const grouped = data.reduce((acc, item) => {
      const hour = new Date(item.sim_datetime).getHours();
      const hourString = `${hour.toString().padStart(2, "0")}:00`;
      if (!acc[hourString]) {
        acc[hourString] = { total: 0 };
      }
      acc[hourString].total++;
      return acc;
    }, {});

    // Asegura todas las horas del día
    for (let i = 0; i < 24; i++) {
      const hourString = `${i.toString().padStart(2, "0")}:00`;
      if (!grouped[hourString]) {
        grouped[hourString] = { total: 0 };
      }
    }

    return Object.entries(grouped).sort(([hourA], [hourB]) =>
      hourA.localeCompare(hourB),
    );
  };

  // NUEVA función para procesar logins por hora
  const processLoginData = (data) => {
    // data: { "0": 29, ... }
    // Asegura todas las horas del día y convierte a array ordenado
    const result = [];
    for (let i = 0; i < 24; i++) {
      const hourString = `${i.toString().padStart(2, "0")}:00`;
      result.push([hourString, data[i] || 0]);
    }
    return result;
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction:
      viewMode === "historic"
        ? {
            mode: "nearest",
            axis: "x",
            intersect: false,
          }
        : undefined,
    plugins: {
      legend: {
        display: viewMode !== "historic" && viewMode !== "logins",
        position: "top",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          font: {
            family: "'Inter', sans-serif",
            size: 12,
          },
        },
      },
      title: {
        display: true,
        text:
          viewMode === "daily"
            ? "Distribución de Categorías por Fecha"
            : viewMode === "hourly"
              ? "Distribución de Categorías por Hora"
              : viewMode === "historic"
                ? "Distribución de Actividades por Hora"
                : "Logins por Hora",
        font: {
          family: "'Inter', sans-serif",
          size: 16,
          weight: "600",
        },
        padding: {
          top: 10,
          bottom: 30,
        },
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        titleColor: "#1f2937",
        bodyColor: "#4b5563",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        padding: 12,
        bodyFont: {
          family: "'Inter', sans-serif",
        },
        titleFont: {
          family: "'Inter', sans-serif",
          weight: "600",
        },
        displayColors: true,
        usePointStyle: true,
        boxPadding: 6,
        callbacks: {
          title: function (context) {
            const label = context[0].label;
            if (viewMode === "daily") return label;
            if (viewMode === "historic" || viewMode === "logins") {
              return `Hora: ${label}`;
            }
            if (viewMode === "hourly") {
              return `Hora: ${label}`;
            }
            return label;
          },
          label: function (context) {
            if (viewMode === "historic") {
              return `Total de actividades: ${context.parsed.y}`;
            }
            if (viewMode === "logins") {
              return `Logins: ${context.parsed.y}`;
            }
            if (viewMode === "daily" || viewMode === "hourly") {
              return `${context.dataset.label}: ${context.parsed.y}`;
            }
            return context.formattedValue;
          },
        },
      },
      // Habilita zoom y pan para todas las vistas
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: true,
          },
          mode: "x",
        },
        pan: {
          enabled: true,
          mode: "x",
        },
        limits: {
          x: { min: "original", max: "original" },
        },
      },
    },
    elements:
      viewMode === "historic" || viewMode === "logins"
        ? {
            line: {
              tension: 0.6,
              borderWidth: 2,
            },
            point: {
              radius: 4, // Mostrar bolitas siempre
              hoverRadius: 6,
            },
          }
        : viewMode === "hourly"
          ? {
              line: {
                tension: 0.3,
              },
            }
          : {
              bar: {
                borderSkipped: false,
              },
            },
    scales: {
      x: {
        stacked: viewMode === "daily",
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11,
          },
          maxRotation: 0,
          minRotation: 0,
          callback: function (value, index, values) {
            if (viewMode === "hourly") {
              return this.getLabelForValue(value);
            }
            return this.getLabelForValue(value);
          },
        },
      },
      y: {
        stacked: viewMode === "daily",
        grid: {
          color: "#f3f4f6",
          drawBorder: false,
        },
        border: {
          display: false,
        },
        ticks: {
          font: {
            family: "'Inter', sans-serif",
            size: 11,
          },
          padding: 10,
          stepSize: 1,
          callback: function (value) {
            return Number.isInteger(value) ? value : null;
          },
        },
        // Añadir configuración específica para vista histórica
        ...(viewMode === "historic" || viewMode === "logins"
          ? {
              suggestedMax: (context) => {
                const values = context.chart.data.datasets[0].data;
                const max = Math.max(...values);
                return Math.max(10, max + 2); // El máximo entre 10 o el valor más alto + 2
              },
            }
          : {}),
      },
    },
  };

  const chartData =
    viewMode === "logins"
      ? {
          labels: loginData.map(([label]) => label),
          datasets: [
            {
              //label: 'Logins por Hora',
              data: loginData.map(([, count]) => count),
              borderColor: "#6366f1",
              backgroundColor: "#6366f1",
              fill: "origin",
              tension: 0.6,
              pointRadius: 4,
              pointHoverRadius: 6,
              borderWidth: 2,
              spanGaps: false,
            },
          ],
        }
      : {
          labels: histData.map(([label]) => label),
          datasets:
            viewMode === "historic"
              ? [
                  {
                    label: "Total por Hora",
                    data: histData.map(([, counts]) => counts.total),
                    borderColor: "#f1666d", // rojo claro
                    backgroundColor: "#f1666d", // rojo claro sólido
                    fill: "origin",
                    tension: 0.6,
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    borderWidth: 2,
                    spanGaps: false,
                  },
                ]
              : [
                  {
                    label: "Protagonista",
                    data: histData.map(([, counts]) => counts.Protagonista),
                    ...(viewMode === "hourly"
                      ? {
                          borderColor: "#610000",
                          backgroundColor: "rgba(97, 0, 0, 0.15)",
                          pointBackgroundColor: "#610000",
                          pointBorderColor: "#610000",
                          pointRadius: 4,
                          pointHoverRadius: 6,
                          fill: false,
                        }
                      : {
                          backgroundColor: "#610000",
                          hoverBackgroundColor: "#9c0720",
                          // Sin borde
                          borderRadius: 0,
                        }),
                  },
                  {
                    label: "Conocedor",
                    data: histData.map(([, counts]) => counts.Conocedor),
                    ...(viewMode === "hourly"
                      ? {
                          borderColor: "#9c0720",
                          backgroundColor: "rgba(156, 7, 32, 0.15)",
                          pointBackgroundColor: "#9c0720",
                          pointBorderColor: "#9c0720",
                          pointRadius: 4,
                          pointHoverRadius: 6,
                          fill: false,
                        }
                      : {
                          backgroundColor: "#9c0720",
                          hoverBackgroundColor: "#dc143c",
                          // Sin borde
                          borderRadius: 0,
                        }),
                  },
                  {
                    label: "Explorador",
                    data: histData.map(([, counts]) => counts.Explorador),
                    ...(viewMode === "hourly"
                      ? {
                          borderColor: "#dc143c",
                          backgroundColor: "rgba(220, 20, 60, 0.15)",
                          pointBackgroundColor: "#dc143c",
                          pointBorderColor: "#dc143c",
                          pointRadius: 4,
                          pointHoverRadius: 6,
                          fill: false,
                        }
                      : {
                          backgroundColor: "#dc143c",
                          hoverBackgroundColor: "#f1666d",
                          // Sin borde
                          borderRadius: 0,
                        }),
                  },
                  {
                    label: "Espectador",
                    data: histData.map(([, counts]) => counts.Espectador),
                    ...(viewMode === "hourly"
                      ? {
                          borderColor: "#f1666d",
                          backgroundColor: "rgba(241, 102, 109, 0.15)",
                          pointBackgroundColor: "#f1666d",
                          pointBorderColor: "#f1666d",
                          pointRadius: 4,
                          pointHoverRadius: 6,
                          fill: false,
                        }
                      : {
                          backgroundColor: "#f1666d",
                          hoverBackgroundColor: "#ff9ea2",
                          // Sin borde
                          borderRadius: 0,
                        }),
                  },
                ].map((dataset, index) => ({
                  ...dataset,
                  ...(viewMode === "daily"
                    ? {
                        borderRadius:
                          index === 0
                            ? {
                                topLeft: 6,
                                topRight: 6,
                                bottomLeft: 0,
                                bottomRight: 0,
                              }
                            : 0,
                      }
                    : {}),
                })),
        };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-8 w-full max-w-5xl shadow-2xl">
        <div className="flex justify-between items-center mb-3">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Histograma de Categorías
            </h2>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("daily")}
                className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${
                  viewMode === "daily"
                    ? "bg-white shadow-sm text-gray-800"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
              >
                Por Día
              </button>
              <button
                onClick={() => setViewMode("hourly")}
                className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${
                  viewMode === "hourly"
                    ? "bg-white shadow-sm text-gray-800"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
              >
                Por Hora
              </button>
              <button
                onClick={() => setViewMode("historic")}
                className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${
                  viewMode === "historic"
                    ? "bg-white shadow-sm text-gray-800"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
              >
                Histórico
              </button>
              <button
                onClick={() => setViewMode("logins")}
                className={`px-3 py-1 text-sm rounded-md transition-colors duration-200 ${
                  viewMode === "logins"
                    ? "bg-white shadow-sm text-gray-800"
                    : "text-gray-600 hover:bg-gray-200"
                }`}
              >
                Logins por Hora
              </button>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="mb-6 px-4 py-2 bg-gradient-to-r from-purple-50 to-purple-100 rounded-full border border-purple-300">
          <p className="text-sm text-purple-700 font-medium">
            Pase el mouse sobre la gráfica para descubrir más información
          </p>
        </div>
        <div className="h-[60vh]">
          {viewMode === "daily" ? (
            <Bar options={options} data={chartData} />
          ) : (
            // Siempre usa Line para histórico y logins, fill: true lo hace área
            <Line options={options} data={chartData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Histograma;
